import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button, Badge, Collapsible, CollapsibleTrigger, CollapsibleContent } from '@design-system';
import { cn } from '@client/lib';
import { ALL_POLICY_CATEGORIES, POLICY_CATEGORY_LABELS } from '@shared/guidedPlanningPreferences';
import { formatThousands } from '@shared/formatThousands';

import { useSession, useIsSessionComplete } from '@/features/guided-financial-planning/context';
import { useQuoteFilters } from '@/features/guided-financial-planning/hooks';

interface FilterChipProps {
  label: string;
  isSelected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

function FilterChip({ label, isSelected, onToggle, disabled }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium transition-all',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        isSelected
          ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
          : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50',
      )}
    >
      {label}
      {isSelected && <X className="size-3" />}
    </button>
  );
}

interface QuoteFiltersPanelProps {
  quoteCount: number;
  isRefetching?: boolean;
}

export function QuoteFiltersPanel({ quoteCount, isRefetching = false }: QuoteFiltersPanelProps) {
  const { session } = useSession();
  const isSessionComplete = useIsSessionComplete();
  const {
    categories,
    isEditing,
    hasUnsavedChanges,
    isSaving,
    startEditing,
    cancelEditing,
    saveFilters,
    toggleCategory,
  } = useQuoteFilters(session);

  const showLoading = isSaving || isRefetching;

  // Count active filters vs total available
  const totalCategories = ALL_POLICY_CATEGORIES.length;
  const activeCategories = categories.length;

  const handleSave = async () => {
    try {
      await saveFilters();
    } catch (error) {
      toast.error('Failed to save filters. Please try again.');
      console.error('Failed to save filters:', error);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      startEditing();
    } else {
      cancelEditing();
    }
  };

  if (isSessionComplete) {
    return null;
  }

  return (
    <Collapsible
      open={isEditing}
      onOpenChange={handleOpenChange}
    >
      <div
        className={cn('transition-all', isEditing && 'rounded-xl border border-gray-200 bg-white')}
      >
        {/* Trigger - Edit Filters button */}
        <div className={cn('flex items-center gap-2', isEditing && 'px-4 py-2')}>
          {isEditing ? (
            <>
              <span className="text-sm font-medium text-gray-900">Edit Policy Types</span>
              {showLoading && <Loader2 className="size-3.5 animate-spin text-gray-400" />}
            </>
          ) : (
            <>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={showLoading}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Edit Policy Types
                  {!showLoading && (
                    <Badge
                      variant="secondary"
                      className="bg-primary/10 text-primary text-xs"
                    >
                      {formatThousands(quoteCount)} results
                    </Badge>
                  )}
                </Button>
              </CollapsibleTrigger>
              {showLoading && <Loader2 className="size-4 animate-spin text-gray-400" />}
            </>
          )}
        </div>

        {/* Collapsible Content */}
        <CollapsibleContent>
          {/* Filter Content */}
          <div className="space-y-4 border-t border-gray-100 px-4 py-4">
            {/* Policy Categories */}
            <div>
              <div className="mb-2">
                <span className="text-sm font-medium text-gray-700">Policy Types</span>
                <span className="ml-2 text-xs text-gray-500">
                  {activeCategories} of {totalCategories}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ALL_POLICY_CATEGORIES.map(category => (
                  <FilterChip
                    key={category}
                    label={POLICY_CATEGORY_LABELS[category]}
                    isSelected={categories.includes(category)}
                    onToggle={() => toggleCategory(category)}
                    disabled={showLoading}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Footer with Cancel, Save */}
          <div className="flex items-center justify-end border-t border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={showLoading}
                  className="text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </Button>
              </CollapsibleTrigger>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!hasUnsavedChanges || showLoading}
              >
                {showLoading ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : null}
                Save
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
